/**
 * Feature reads for the frontend.
 *
 * The map arrives already resolved on `GET /me` — the backend decides what
 * this user can see, and the frontend only renders the answer. Features are
 * deliberately NOT carried in `config.json`: a second source would drift from
 * the backend, and the whole point of the mechanism is one answer per user.
 *
 * Because the auth context is shell-side and shared-core cannot import from a
 * shell, registration follows the existing provider pattern in
 * `data/api/providers.ts` — the shell calls `setFeatures` once `getMe()`
 * resolves, exactly as it registers the auth token provider today.
 *
 * `isFeatureEnabled` returns `false` for an absent key, while the backend
 * *raises* on an undeclared feature. That asymmetry is deliberate: `/me` carries
 * only the features that are on for this caller, so "absent" and "off" are the
 * same thing here and a user cannot enumerate the unreleased roadmap from a
 * response body. `scripts/check_features.py` scans `isFeatureEnabled("…")`
 * call sites against `modules/features.yaml`, so a typo fails at check
 * time rather than becoming a permanently invisible feature.
 *
 * Platform-neutral: no browser globals, no DOM, no routing.
 */

/**
 * Every feature declared in `modules/features.yaml`.
 *
 * Hand-maintained against that file, which stays the single source of truth
 * for *where* each feature is enabled — this union carries only the names.
 * `scripts/check_features.py` checks the pair in both directions: a route
 * naming a feature absent from the YAML is an error, and a declared feature
 * that no route claims is a dead entry.
 *
 * The union exists so `routes.ts` can make `feature` required and typed. A new
 * route without one is a compile error, and a misspelled one is too — which is
 * the property the production surface rests on. Coverage is total, so nothing
 * can be live in production merely because someone forgot to think about it.
 */
export type FeatureName =
  | "account_allocation"
  | "accruals_and_prepayments"
  | "admin_access"
  | "admin_ops"
  | "artwork_combined_file"
  | "brandhub_channel"
  | "catalogue"
  | "catalogue_customer_quoting"
  | "claude_tools"
  | "claude_tools_write"
  | "claude_feedback"
  | "claude_destructive_confirm"
  | "one_mailbox"
  | "made_despatches_from_tharstern"
  | "supplier_approval_gate"
  | "commercial_exceptions"
  | "carriage_cost_capture"
  | "communications"
  | "csr_home"
  | "customer_address_book_enforced"
  | "customer_delivery_dates"
  | "customer_delivery_updates"
  | "customer_get_access"
  | "customer_link_access"
  | "customer_option_wording"
  | "customer_portal"
  | "customers"
  | "dev_tooling"
  | "draft_quote_builder"
  | "estimates_create"
  | "estimates_view"
  | "feedback"
  | "insights"
  | "jobs_view"
  | "journey_testing"
  | "leads"
  | "marketing"
  | "merchandise_draft_estimates"
  | "one"
  | "order_line_commands"
  | "orders"
  | "planning"
  | "portal_theme"
  | "previews"
  | "production_ops"
  | "promo_office_channel"
  | "promo_office_visibility"
  | "prospects"
  | "purchase_invoice_matching"
  | "quotes"
  | "recommendations"
  | "recommendations_config"
  | "recommendations_config_edit"
  | "sales_home"
  | "sales_ledger"
  | "settings_home"
  | "shopfloor"
  | "stock_control"
  | "stock_cutover"
  | "stock_replenishment"
  | "supplier_assessment_requests"
  | "supplier_portal"
  | "tharstern_artwork_archive"
  | "tharstern_history"
  | "tharstern_job_check"
  | "tickets"
  | "tickets_config"
  | "v1_completion";

let features: Readonly<Record<string, boolean>> = Object.freeze({});
let allEnabled = false;

/**
 * Register the resolved map. Called by the shell's auth provider after
 * `getMe()` resolves, and again on sign-out with `{}` so a subsequent user
 * never inherits the previous one's features.
 */
export function setFeatures(next: Record<string, boolean>): void {
  features = Object.freeze({ ...next });
  allEnabled = false;
}

/**
 * Resolve every feature as enabled. **Mock mode only.**
 *
 * Mock mode runs with no backend at all — `main.tsx` renders `MockAuthProvider`
 * against the mock adapter, so there is no `/me` and therefore no resolved map.
 * Without this the default `{}` would read as "every feature off", and every
 * gated page would render the not-found page: Storybook, the Playwright suite
 * and any mock-data preview would show an app with nothing in it.
 *
 * "Everything on" is the honest answer there rather than a convenience. Mock
 * mode is the `dev` experience with fixtures instead of a database, and every
 * feature is on in `dev` — see `modules/features.yaml`. It is deliberately
 * NOT a way to reveal a feature: there is nothing behind it to reveal, because
 * the real API is not in the loop. The API-side `require_feature` is the gate
 * that protects anything, and it is unreachable from here.
 */
export function enableAllFeaturesForMockMode(): void {
  features = Object.freeze({});
  allEnabled = true;
}

/** Is this feature visible to the signed-in user? */
export function isFeatureEnabled(name: string): boolean {
  return allEnabled || features[name] === true;
}

/**
 * Every feature currently on for this user. For diagnostics and stories — page
 * code should ask `isFeatureEnabled` about a specific feature rather than
 * iterating, so the check script can see which features the code depends on.
 */
export function enabledFeatures(): Readonly<Record<string, boolean>> {
  return features;
}
